import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  Building2, 
  MapPin, 
  Clock, 
  CheckCircle, 
  Eye, 
  EyeOff,
  RefreshCw,
  Search,
  Filter,
  MessageSquare,
  Users,
  TrendingUp,
  Calendar
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchEnquiries = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/enquiries`);
      setEnquiries(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      setError('Failed to load enquiries');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const updateEnquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status });
      setEnquiries(enquiries.map(enq => 
        enq.id === id ? { ...enq, status } : enq
      ));
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry({ ...selectedEnquiry, status });
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEnquiries = enquiries
    .filter(enq => {
      if (filter === 'all') return true;
      return enq.status === filter;
    })
    .filter(enq => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        enq.name?.toLowerCase().includes(term) ||
        enq.email?.toLowerCase().includes(term) ||
        enq.company?.toLowerCase().includes(term) ||
        enq.message?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const stats = {
    total: enquiries.length,
    new: enquiries.filter(e => e.status === 'new').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    closed: enquiries.filter(e => e.status === 'closed').length,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'contacted': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'closed': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-main flex items-center justify-center" data-testid="admin-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-primary-main animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading enquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-main" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-primary-dark text-white py-6 px-6" data-testid="admin-header">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold">
                Admin Dashboard
              </h1>
              <p className="text-white/70 text-sm mt-1">Manage enquiries and leads</p>
            </div>
            <button
              onClick={fetchEnquiries}
              disabled={refreshing}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 transition-colors"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-primary-main/10 p-6"
            data-testid="stat-total"
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary-main/10 p-3 rounded-full">
                <Users className="w-5 h-5 text-primary-main" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary-main">{stats.total}</p>
                <p className="text-xs text-text-muted uppercase tracking-wider">Total</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-blue-200 p-6"
            data-testid="stat-new"
          >
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-full">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
                <p className="text-xs text-text-muted uppercase tracking-wider">New</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-yellow-200 p-6"
            data-testid="stat-contacted"
          >
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 p-3 rounded-full">
                <TrendingUp className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.contacted}</p>
                <p className="text-xs text-text-muted uppercase tracking-wider">Contacted</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-green-200 p-6"
            data-testid="stat-closed"
          >
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.closed}</p>
                <p className="text-xs text-text-muted uppercase tracking-wider">Closed</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white border border-primary-main/10 p-4 mb-6" data-testid="filters-section">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Search by name, email, company, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-primary-main/20 focus:border-primary-main focus:outline-none font-body text-sm"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-text-muted" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-primary-main/20 px-4 py-2 focus:border-primary-main focus:outline-none font-body text-sm"
                data-testid="filter-select"
              >
                <option value="all">All Enquiries</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-6" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Enquiries List */}
          <div className="lg:col-span-2" data-testid="enquiries-list">
            <div className="bg-white border border-primary-main/10">
              <div className="border-b border-primary-main/10 p-4">
                <h2 className="font-heading text-lg font-bold text-primary-main">
                  Enquiries ({filteredEnquiries.length})
                </h2>
              </div>

              {filteredEnquiries.length === 0 ? (
                <div className="p-8 text-center" data-testid="no-enquiries">
                  <MessageSquare className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary">No enquiries found</p>
                </div>
              ) : (
                <div className="divide-y divide-primary-main/10 max-h-[600px] overflow-y-auto">
                  {filteredEnquiries.map((enquiry, index) => (
                    <motion.div
                      key={enquiry.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSelectedEnquiry(enquiry)}
                      className={`p-4 cursor-pointer hover:bg-background-main transition-colors ${
                        selectedEnquiry?.id === enquiry.id ? 'bg-primary-main/5 border-l-4 border-l-primary-main' : ''
                      }`}
                      data-testid={`enquiry-item-${enquiry.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-primary-main truncate">
                              {enquiry.name}
                            </h3>
                            <span className={`text-xs px-2 py-0.5 border ${getStatusColor(enquiry.status)}`}>
                              {enquiry.status}
                            </span>
                          </div>
                          <p className="text-sm text-text-secondary truncate">{enquiry.email}</p>
                          <p className="text-sm text-text-muted truncate mt-1">
                            {enquiry.message?.substring(0, 80)}...
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-text-muted">
                            {formatDate(enquiry.created_at)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Enquiry Detail */}
          <div className="lg:col-span-1" data-testid="enquiry-detail">
            {selectedEnquiry ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white border border-primary-main/10 sticky top-4"
              >
                <div className="border-b border-primary-main/10 p-4">
                  <h2 className="font-heading text-lg font-bold text-primary-main">
                    Enquiry Details
                  </h2>
                </div>

                <div className="p-4 space-y-4">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3" data-testid="detail-name">
                      <div className="bg-primary-main/10 p-2 rounded-full">
                        <Users className="w-4 h-4 text-primary-main" />
                      </div>
                      <div>
                        <p className="text-xs text-text-muted uppercase">Name</p>
                        <p className="font-semibold text-primary-main">{selectedEnquiry.name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3" data-testid="detail-email">
                      <div className="bg-primary-main/10 p-2 rounded-full">
                        <Mail className="w-4 h-4 text-primary-main" />
                      </div>
                      <div>
                        <p className="text-xs text-text-muted uppercase">Email</p>
                        <a 
                          href={`mailto:${selectedEnquiry.email}`} 
                          className="text-secondary-main hover:underline"
                        >
                          {selectedEnquiry.email}
                        </a>
                      </div>
                    </div>

                    {selectedEnquiry.phone && (
                      <div className="flex items-center gap-3" data-testid="detail-phone">
                        <div className="bg-primary-main/10 p-2 rounded-full">
                          <Phone className="w-4 h-4 text-primary-main" />
                        </div>
                        <div>
                          <p className="text-xs text-text-muted uppercase">Phone</p>
                          <a 
                            href={`tel:${selectedEnquiry.phone}`} 
                            className="text-secondary-main hover:underline"
                          >
                            {selectedEnquiry.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3" data-testid="detail-company">
                      <div className="bg-primary-main/10 p-2 rounded-full">
                        <Building2 className="w-4 h-4 text-primary-main" />
                      </div>
                      <div>
                        <p className="text-xs text-text-muted uppercase">Company</p>
                        <p className="text-text-primary">{selectedEnquiry.company}</p>
                      </div>
                    </div>

                    {selectedEnquiry.country && (
                      <div className="flex items-center gap-3" data-testid="detail-country">
                        <div className="bg-primary-main/10 p-2 rounded-full">
                          <MapPin className="w-4 h-4 text-primary-main" />
                        </div>
                        <div>
                          <p className="text-xs text-text-muted uppercase">Country</p>
                          <p className="text-text-primary">{selectedEnquiry.country}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3" data-testid="detail-date">
                      <div className="bg-primary-main/10 p-2 rounded-full">
                        <Calendar className="w-4 h-4 text-primary-main" />
                      </div>
                      <div>
                        <p className="text-xs text-text-muted uppercase">Submitted</p>
                        <p className="text-text-primary">{formatDate(selectedEnquiry.created_at)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="pt-4 border-t border-primary-main/10" data-testid="detail-message">
                    <p className="text-xs text-text-muted uppercase mb-2">Message</p>
                    <p className="text-text-primary text-sm leading-relaxed bg-background-main p-3">
                      {selectedEnquiry.message}
                    </p>
                  </div>

                  {/* Status Actions */}
                  <div className="pt-4 border-t border-primary-main/10" data-testid="status-actions">
                    <p className="text-xs text-text-muted uppercase mb-3">Update Status</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateEnquiryStatus(selectedEnquiry.id, 'new')}
                        className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                          selectedEnquiry.status === 'new' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        data-testid="status-new-btn"
                      >
                        New
                      </button>
                      <button
                        onClick={() => updateEnquiryStatus(selectedEnquiry.id, 'contacted')}
                        className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                          selectedEnquiry.status === 'contacted' 
                            ? 'bg-yellow-600 text-white' 
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                        data-testid="status-contacted-btn"
                      >
                        Contacted
                      </button>
                      <button
                        onClick={() => updateEnquiryStatus(selectedEnquiry.id, 'closed')}
                        className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                          selectedEnquiry.status === 'closed' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                        data-testid="status-closed-btn"
                      >
                        Closed
                      </button>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="pt-4 border-t border-primary-main/10" data-testid="quick-actions">
                    <p className="text-xs text-text-muted uppercase mb-3">Quick Actions</p>
                    <div className="space-y-2">
                      <a
                        href={`mailto:${selectedEnquiry.email}?subject=Re: Your GrowthPop Enquiry`}
                        className="block w-full bg-primary-main text-white text-center py-3 text-xs font-semibold uppercase tracking-wider hover:bg-primary-light transition-colors"
                        data-testid="reply-email-btn"
                      >
                        <Mail className="w-4 h-4 inline mr-2" />
                        Reply via Email
                      </a>
                      {selectedEnquiry.phone && (
                        <a
                          href={`https://wa.me/${selectedEnquiry.phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full bg-green-600 text-white text-center py-3 text-xs font-semibold uppercase tracking-wider hover:bg-green-700 transition-colors"
                          data-testid="whatsapp-btn"
                        >
                          <MessageSquare className="w-4 h-4 inline mr-2" />
                          WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-white border border-primary-main/10 p-8 text-center" data-testid="no-selection">
                <Eye className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">Select an enquiry to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
